import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useDashboard } from '../../context/DashboardContext';
import { usePortfolioView } from '../../context/PortfolioViewContext';
import { selectAllocations, selectPortfolioSummary } from '../../state/dashboardSelectors';
import type { Allocation } from '../../state/dashboardSelectors';
import type { TimeWindow } from '../../services/dashboardApi';

// Color palette for donut chart
const COLORS = ['#BFDBFE', '#FECACA', '#BBF7D0', '#FDE68A', '#E9D5FF']; // pastel blue, red, green, yellow, purple

// Custom tooltip component for pie chart with logo
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: Allocation;
    value: number;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null;
  
  const data = payload[0].payload;
  const value = payload[0].value;
  
  return (
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        fontSize: '12px',
        padding: '6px 10px',
      }}
      className="whitespace-nowrap"
    >
      <div className="flex items-center space-x-2">
        {data.logoUrl && (
          <img
            src={data.logoUrl}
            alt=""
            className="w-4 h-4 rounded-full object-cover flex-shrink-0"
          />
        )}
        <span>{data.name}: {value.toFixed(1)}%</span>
      </div>
    </div>
  );
};

interface PortfolioSummaryCardProps {
  onEditClick: () => void;
  hideDollarSection?: boolean; // For friend portfolio views
  className?: string;
}

/**
 * PortfolioSummaryCard displays the portfolio summary including:
 * - Mini donut chart showing account allocations
 * - Past day performance percentage
 * - Selected time window performance percentage
 * - Dollar amount change for selected time window
 */
export const PortfolioSummaryCard: React.FC<PortfolioSummaryCardProps> = ({
  onEditClick,
  hideDollarSection = false,
  className = '',
}) => {
  const { data, isFetching } = useDashboard();
  const { selectedTimeWindow } = usePortfolioView();
  
  const allocations = useMemo(
    () => selectAllocations(data),
    [data]
  );
  
  const summary = useMemo(
    () => selectPortfolioSummary(data, selectedTimeWindow.key as TimeWindow),
    [data, selectedTimeWindow.key]
  );
  
  const { pastDayPct, windowPct, windowDollar } = summary;
  const windowLabel = selectedTimeWindow.label;
  return (
    <div className={`bg-white rounded-lg p-6 shadow-sm ${className}`}>
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-green-700">
          Portfolio Summary
        </h2>
      </div>

      <div className="flex items-center justify-around">
        {/* Mini Donut Chart */}
        <div className="w-16 h-16">
          {isFetching && allocations.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            </div>
          ) : allocations.length > 0 ? (
            <PieChart width={64} height={64}>
              <Pie
                data={allocations}
                cx="50%"
                cy="50%"
                innerRadius={20}
                outerRadius={28}
                dataKey="value"
                nameKey="name"
                paddingAngle={2}
              >
                {allocations.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                cursor={false}
                position={{ x: 80, y: 32 }}
              />
            </PieChart>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
              —
            </div>
          )}
        </div>

        {/* Past Day Performance */}
        <div className="text-center">
          <h3 className="text-xs sm:text-sm text-gray-500">Past day</h3>
          {isFetching && pastDayPct == null ? (
            <div className="flex items-center justify-center h-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
            </div>
          ) : (
            <p
              className={`text-lg sm:text-2xl font-semibold ${
                pastDayPct == null
                  ? 'text-gray-400'
                  : pastDayPct >= 0
                  ? 'text-green-500'
                  : 'text-red-500'
              }`}
            >
              {pastDayPct != null
                ? `${pastDayPct >= 0 ? '+' : ''}${pastDayPct.toFixed(1)}%`
                : '—'}
            </p>
          )}
        </div>

        {/* Time Window Performance */}
        <div className="text-center">
          <h3 className="text-xs sm:text-sm">
            <span className="text-gray-500">Past </span>
            <span className="text-blue-500">{windowLabel}</span>
          </h3>
          {isFetching && windowPct == null ? (
            <div className="flex items-center justify-center h-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
            </div>
          ) : (
            <p
              className={`text-lg sm:text-2xl font-semibold ${
                windowPct == null
                  ? 'text-gray-400'
                  : windowPct >= 0
                  ? 'text-green-500'
                  : 'text-red-500'
              }`}
            >
              {windowPct != null
                ? `${windowPct >= 0 ? '+' : ''}${windowPct.toFixed(1)}%`
                : '—'}
            </p>
          )}
        </div>

        {/* Dollar Amount Change - Hidden for friend portfolio */}
        {!hideDollarSection && (
          <div className="flex flex-col items-center">
            <h3 className="text-xs sm:text-sm text-gray-500">
              My total <span className="text-blue-500">{windowLabel}</span>
            </h3>
            {isFetching && windowDollar == null ? (
              <div className="flex items-center justify-center h-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <p
                className={`text-lg sm:text-2xl font-semibold ${
                  windowDollar == null
                    ? 'text-gray-400'
                    : windowDollar >= 0
                    ? 'text-green-500'
                    : 'text-red-500'
                }`}
              >
                {windowDollar != null
                  ? `${windowDollar >= 0 ? '+' : '-'}$${Math.abs(windowDollar).toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}`
                  : '—'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

